import { createFile } from '../utils/fs.js'
import { defaultAdkFolder } from '../const.js'
import path from 'path'
import { AssetsManager, AssetsCacheManager } from '../assets/index.js'

export async function generateAssetsTypes(projectPath: string): Promise<void> {
  const assetsManager = new AssetsManager({ projectPath })

  // Generate TypeScript types for assets
  const typesCode = await assetsManager.generateTypes()

  // Write to the types directory
  const typesPath = path.join(projectPath, defaultAdkFolder, 'assets.d.ts')
  await createFile(typesPath, typesCode)
}

export async function generateAssetsRuntime(projectPath: string, botId?: string, workspaceId?: string): Promise<void> {
  const assetsManager = new AssetsManager({ projectPath, botId, workspaceId })

  // Get enriched assets with remote metadata when available
  const enrichedAssets = await assetsManager.getEnrichedLocalAssets()

  // Generate hardcoded asset metadata
  const assetsMap: Record<string, any> = {}
  const localHashesMap: Record<string, string> = {}

  // Read cache to get local hashes
  const cacheManager = new AssetsCacheManager(projectPath)
  const cache = await cacheManager.load()

  for (const asset of enrichedAssets) {
    assetsMap[asset.path] = asset
    const cacheEntry = cache.entries[asset.path]
    if (cacheEntry) {
      localHashesMap[asset.path] = cacheEntry.localHash
    }
  }

  // Generate minimal runtime code that only exports metadata
  const runtimeCode = `
// Auto-generated assets metadata
import { Asset, initAssets } from '@botpress/runtime/runtime';

// Static asset metadata (populated at build time)
export const assetsMetadata: Record<string, Asset> = ${JSON.stringify(assetsMap, null, 2)};

// Local hashes from cache
export const localHashes: Record<string, string> = ${JSON.stringify(localHashesMap, null, 2)};

// Initialize the assets runtime with metadata and local hashes
// The global object should be passed by the agent initialization code
export function initializeAssets(globalObj: any = globalThis) {
  initAssets(globalObj, assetsMetadata, localHashes);
}

// Auto-initialize if running in a supported environment
if (typeof globalThis !== 'undefined') {
  initializeAssets(globalThis);
} else if (typeof global !== 'undefined') {
  initializeAssets(global);
}
`

  const runtimePath = path.join(projectPath, defaultAdkFolder, 'assets-runtime.ts')
  await createFile(runtimePath, runtimeCode)
}

export async function initAssets(projectPath: string, botId?: string, workspaceId?: string): Promise<void> {
  const assetsManager = new AssetsManager({ projectPath, botId, workspaceId })

  if (await assetsManager.hasAssetsDirectory()) {
    await generateAssetsTypes(projectPath)
    await generateAssetsRuntime(projectPath, botId, workspaceId)
  } else {
    // Generate empty types if no assets directory exists
    const emptyTypesCode = `// No assets directory found
import { Asset } from '@botpress/runtime';

export type AssetPaths = never;

export interface AssetPathMap {}

// Runtime asset access
declare global {
  const assets: {
    get<T extends AssetPaths>(path: T): Asset;
    list(): Asset[];
    getSyncStatus(): { 
      synced: boolean; 
      neverSynced: string[]; 
      stale: string[];
      upToDate: string[];
    };
  };
}
`

    const typesPath = path.join(projectPath, defaultAdkFolder, 'assets.d.ts')
    await createFile(typesPath, emptyTypesCode)

    // Generate empty runtime
    const emptyRuntimeCode = `
// No assets available
import { Asset, initAssets } from '@botpress/runtime';

// Empty asset metadata
export const assetsMetadata: Record<string, Asset> = {};

// Initialize with empty metadata
export function initializeAssets(globalObj: any = globalThis) {
  initAssets(globalObj, assetsMetadata);
}

// Auto-initialize if running in a supported environment
if (typeof globalThis !== 'undefined') {
  initializeAssets(globalThis);
} else if (typeof global !== 'undefined') {
  initializeAssets(global);
}
`

    const runtimePath = path.join(projectPath, defaultAdkFolder, 'assets-runtime.ts')
    await createFile(runtimePath, emptyRuntimeCode)
  }
}
